import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Event } from '@/graphql/generatedTypes';

const getInitials = (name?: string | null) =>
  name
    ?.split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

/**
 * Event picker rendered as the header slot of the event-owner context sidebar.
 * Closing the mobile sidebar on selection keeps the previous dashboard
 * behaviour now that it lives inside the shared app shell sidebar.
 */
export function EventSwitcher({
  events = [],
  selectedEvent,
  onEventSelect,
}: {
  events: Event[];
  selectedEvent: Event | undefined;
  onEventSelect: (event: Event | null) => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();

  const handleSelect = (event: Event | null) => {
    onEventSelect(event);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="tw:data-[state=open]:bg-sidebar-accent tw:data-[state=open]:text-sidebar-accent-foreground tw:cursor-pointer"
            >
              <Avatar>
                <AvatarImage src={undefined} className="tw:object-cover" />
                <AvatarFallback className="tw:uppercase tw:bg-lilac-400 tw:text-white">
                  {getInitials(selectedEvent?.name) ?? 'Vy'}
                </AvatarFallback>
              </Avatar>
              <div className="tw:grid tw:flex-1 tw:text-left tw:text-sm tw:leading-tight">
                <span className="tw:truncate tw:font-medium">
                  {selectedEvent?.name ?? 'Select an event'}
                </span>
                {selectedEvent && (
                  <span className="tw:truncate tw:text-xs">
                    {new Date(
                      selectedEvent.start_date ?? 0,
                    ).toLocaleDateString()}{' '}
                    -{' '}
                    {new Date(selectedEvent.end_date ?? 0).toLocaleDateString()}
                  </span>
                )}
              </div>
              <ChevronsUpDown className="tw:ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="tw:w-(--radix-dropdown-menu-trigger-width) tw:min-w-56 tw:rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="tw:text-muted-foreground tw:text-xs">
              Events
            </DropdownMenuLabel>
            {events.map((event) => (
              <DropdownMenuItem
                key={event.id}
                onClick={() => handleSelect(event)}
                className="tw:gap-2 tw:p-2"
              >
                <Avatar className="tw:size-5 tw:border">
                  <AvatarImage src={undefined} className="tw:object-cover" />
                  <AvatarFallback className="tw:text-[8px] tw:uppercase tw:bg-lilac-400 tw:text-black">
                    {getInitials(event?.name) ?? 'E'}
                  </AvatarFallback>
                </Avatar>
                {event.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleSelect(null)}>
              All Events
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
